import type { IPortfolioTreeDraftParams } from '@/types/IPortfolioTree';
import type { RouteLocationNormalized } from 'vue-router';
import { PortfolioVersionType } from '@/constants/PortfolioVersionType';
import { RouteName } from '@/constants/RouteName';

export type GetDraftRequestVersionTypeParams = {
  isPdf: boolean;
  routeName: RouteLocationNormalized['name'];
  routeQueryVersion: PortfolioVersionType | undefined;
  /** Current resolved version from the override or loaded draft response. */
  versionType: PortfolioVersionType | null;
  /** Explicit version pin used by application workflows. */
  versionTypeOverride: PortfolioVersionType | null;
  /** Live version selected from the current route and feature access. */
  livePortfolioVersionTypeToSet: PortfolioVersionType;
};

type GetPdfVersionTypeParams = {
  routeQueryVersion: PortfolioVersionType | undefined;
  versionType: PortfolioVersionType | null;
};

const getPdfVersionType = ({ routeQueryVersion, versionType }: GetPdfVersionTypeParams): PortfolioVersionType => {
  if (routeQueryVersion && Object.values(PortfolioVersionType).includes(routeQueryVersion)) {
    return routeQueryVersion;
  }

  return versionType ?? PortfolioVersionType.POSITION;
};

/**
 * Resolves the version sent on the draft query. This can differ from the
 * currently displayed portfolio version.
 *
 * PDF (one-shot URL load):
 * 1. Use `routeQueryVersion` when it is a valid PortfolioVersionType.
 * 2. Otherwise use the current portfolio version when it is already known.
 * 3. Otherwise default to POSITION.
 *
 * Platform:
 * - Factsheet always requests the route's live version
 *   (`livePortfolioVersionTypeToSet`).
 * - Other routes prefer `versionTypeOverride`, then `livePortfolioVersionTypeToSet`.
 *
 * The current portfolio version is not used on the platform because it can lag onto a previous
 * draft's version (including SIMULATION) while a new draft is in flight, which would
 * trigger extra follow-up requests. PDF does not have that refetch loop.
 */
export const getDraftRequestVersionType = ({
  isPdf,
  routeName,
  routeQueryVersion,
  versionType,
  versionTypeOverride,
  livePortfolioVersionTypeToSet,
}: GetDraftRequestVersionTypeParams): PortfolioVersionType => {
  if (isPdf) {
    // A PDF is a one-time request, so its URL is the source of truth. If the URL
    // has no version, use the version already known for the portfolio.
    return getPdfVersionType({
      routeQueryVersion,
      versionType,
    });
  }

  if (routeName === RouteName.FACTSHEET) {
    // Factsheets always show the route's live version. An override may describe
    // another workflow, so it must not change the version requested here.
    return livePortfolioVersionTypeToSet;
  }

  // On interactive platform routes, keep an explicit override (for example,
  // Simulation or Provisional from the dropdown) pinned. Otherwise request the route's live version
  // instead of reusing the possibly stale version from the previous draft.
  return versionTypeOverride ?? livePortfolioVersionTypeToSet;
};

export type GetDraftParamsArgs = {
  versionTypeToUse: PortfolioVersionType | undefined;
  mode: IPortfolioTreeDraftParams['mode'];
  isPdf: boolean;
  isRiskHistoryMode: boolean | undefined;
  rollingDate: string | undefined;
  constituentAnalysisDate: string | null | undefined;
  periodFromDate: string;
  periodToDate: string;
  routeQueryEndDate: string | undefined;
  doesRouteMatchPortfolioUnderAnalysis: boolean;
  force: Partial<IPortfolioTreeDraftParams> | null | undefined;
};

type GetPositionAnalysisDateParams = {
  isPdf: boolean;
  routeQueryEndDate: string | undefined;
  doesRouteMatchPortfolioUnderAnalysis: boolean;
  constituentAnalysisDate: string | null | undefined;
};

/**
 * Position drafts are created from an analysis date.
 *
 * PDF uses the URL `endDate`. On the platform, reuse the current analysis date only
 * while still on the same portfolio. During a portfolio switch, send undefined so the
 * API so it uses the date from the new draft and a stale global analysis date is not used.
 */
const getPositionAnalysisDate = ({
  isPdf,
  routeQueryEndDate,
  doesRouteMatchPortfolioUnderAnalysis,
  constituentAnalysisDate,
}: GetPositionAnalysisDateParams): string | undefined => {
  if (isPdf && routeQueryEndDate != null) {
    return routeQueryEndDate;
  }

  return doesRouteMatchPortfolioUnderAnalysis ? (constituentAnalysisDate ?? undefined) : undefined;
};

/**
 * Builds the request params for the portfolio draft query from already-resolved
 * primitive values. Kept free of Vue reactivity so it can be unit tested directly.
 *
 * - History version type: creates the portfolio from the position files between two dates.
 * - Position version type: creates the portfolio from the analysis date.
 */
export const getDraftParams = ({
  versionTypeToUse,
  mode,
  isPdf,
  isRiskHistoryMode,
  rollingDate,
  constituentAnalysisDate,
  periodFromDate,
  periodToDate,
  routeQueryEndDate,
  doesRouteMatchPortfolioUnderAnalysis,
  force,
}: GetDraftParamsArgs): IPortfolioTreeDraftParams => {
  const base = {
    // Excel and PDF will use the portfolioVersion from the URL
    // Please note that if Live version is not available, the API will default to returning the Simulation version
    version: versionTypeToUse ?? undefined,
    mode,
  };

  // History version type
  if (versionTypeToUse === PortfolioVersionType.HISTORY) {
    // On the Constituent Risk page, we use the rolling date and constituent analysis date to create the historical portfolio
    if (isRiskHistoryMode && rollingDate && constituentAnalysisDate) {
      return {
        ...base,
        historyDateRange: [rollingDate, constituentAnalysisDate] as [string, string],
        ...force,
      };
    }

    // Send historyDateRange only when it is meaningful: the period is a true range
    if (periodFromDate !== periodToDate) {
      // On a timeseries page, we use the Period to create the historical portfolio
      return {
        ...base,
        historyDateRange: [periodFromDate, periodToDate] as [string, string],
        ...force,
      };
    }
  }

  // Attach analysisDate only for Position. Simulation drafts omit it so changing
  // the analysis date does not refetch the draft.
  if (versionTypeToUse === PortfolioVersionType.POSITION) {
    return {
      ...base,
      analysisDate: getPositionAnalysisDate({
        isPdf,
        routeQueryEndDate,
        doesRouteMatchPortfolioUnderAnalysis,
        constituentAnalysisDate,
      }),
      ...force,
    };
  }

  return {
    ...base,
    ...force,
  };
};
